'use client';

import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePartnerDashboard } from '@/partner/hooks/usePartnerDashboard';
import {
  Sparkles,
  ArrowRight,
  Check,
  Share2,
  Wallet,
  UserCheck,
  Zap,
  TrendingUp,
  ShieldCheck,
  MousePointerClick,
  Award,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

function LoginCTA() {
  return (
    <section id="indique" className="py-14 sm:py-18 md:py-24 bg-muted/10 dark:bg-background border-b border-border/60 transition-colors">
      <div className="container-landing">
        {/* Featured Bento Shell: Destaque premium emoldurado, sem ser extravagante e sem se misturar com o fundo */}
        <div className="relative rounded-3xl lg:rounded-[2.5rem] bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] dark:from-[#091b2e] dark:via-card dark:to-[#05111e] border border-blue-800/30 dark:border-white/10 shadow-2xl p-7 sm:p-10 lg:p-14 overflow-hidden text-white">
          {/* Subtle ambient lighting inside the shell */}
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[110px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-emerald-500/15 rounded-full blur-[100px] pointer-events-none" />

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center relative z-10">
            {/* Left Column: Information & 3-Step Process */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 dark:bg-primary/20 backdrop-blur-md border border-white/20 dark:border-primary/30 text-emerald-300 dark:text-primary text-xs font-semibold uppercase tracking-wider mb-4 shadow-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                <span>Programa Indique e Ganhe</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4 leading-tight">
                Compartilhe saúde. <br />
                <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-400 bg-clip-text text-transparent">
                  Ganhe comissões no PIX.
                </span>
              </h2>

              <p className="text-base sm:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-xl">
                Tenha seu link exclusivo, recomende consultas e profissionais da Medikei e receba bonificações automáticas direto na sua conta a cada atendimento realizado.
              </p>

              {/* 3 Step Sequence: Glass Mini-Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-8">
                <div className="p-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.11] border border-white/10 hover:border-white/20 transition-all backdrop-blur-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-xl bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 font-bold text-xs flex items-center justify-center">
                      01
                    </span>
                    <UserCheck className="w-4 h-4 text-slate-300/70" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Crie sua conta</h3>
                  <p className="text-xs text-slate-300/80 mt-1 leading-snug">
                    Cadastro gratuito e ativação em menos de 1 minuto.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.11] border border-white/10 hover:border-white/20 transition-all backdrop-blur-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-xl bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 font-bold text-xs flex items-center justify-center">
                      02
                    </span>
                    <Share2 className="w-4 h-4 text-slate-300/70" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Envie seu link</h3>
                  <p className="text-xs text-slate-300/80 mt-1 leading-snug">
                    Compartilhe no WhatsApp, redes sociais ou contatos.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.11] border border-white/10 hover:border-white/20 transition-all backdrop-blur-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-xl bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 font-bold text-xs flex items-center justify-center">
                      03
                    </span>
                    <Wallet className="w-4 h-4 text-emerald-300" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Receba no PIX</h3>
                  <p className="text-xs text-slate-300/80 mt-1 leading-snug">
                    Comissão creditada direto na sua conta bancária.
                  </p>
                </div>
              </div>

              {/* CTA & Trust note */}
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Link to="/login" className="inline-block">
                    <Button
                      variant="primary"
                      size="lg"
                      className="bg-white hover:bg-slate-100 text-[#0c2847] font-extrabold text-base px-8 h-12 rounded-xl shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 group w-full sm:w-auto"
                    >
                      <span>Fazer login para começar</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-slate-300/80 flex items-center gap-1.5 pt-1">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Disponível para qualquer pessoa, influenciadores de saúde e empresas parceiras.</span>
                </p>
              </div>
            </div>

            {/* Right Column: Benefits & Earnings Simulator Card */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="w-full max-w-md bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-3xl p-6 sm:p-7 shadow-2xl relative overflow-hidden text-white transition-all">
                {/* Subtle top glow inside the card */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

                {/* Card Header */}
                <div className="flex items-center justify-between pb-4 border-b border-white/15 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-bold tracking-wider uppercase text-slate-300 text-[11px]">
                      Painel do Parceiro
                    </span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold text-[11px] flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Recompensa PIX
                  </span>
                </div>

                {/* Primary Metric: 1st consultation commission */}
                <div className="mt-5 p-4 rounded-2xl bg-black/25 border border-white/10 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-slate-300 block mb-0.5">Comissão por atendimento</span>
                    <div className="flex items-baseline gap-2">
                      <strong className="text-3xl font-black text-white">12%</strong>
                      <span className="text-xs text-slate-300 font-medium">por consulta</span>
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    Na 1ª consulta
                  </span>
                </div>

                {/* Secondary Metrics */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="p-3.5 rounded-2xl bg-white/[0.05] border border-white/10">
                    <div className="flex items-center gap-1.5 text-white font-bold text-lg">
                      <span>5%</span>
                      <span className="text-[11px] font-medium text-emerald-400">+ bônus</span>
                    </div>
                    <p className="text-[11px] text-slate-300 mt-0.5 leading-tight">Recorrência por 6 meses</p>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-white/[0.05] border border-white/10">
                    <div className="flex items-center gap-1.5 text-emerald-300 font-bold text-lg">
                      <span>A partir de R$ 50</span>
                    </div>
                    <p className="text-[11px] text-slate-300 mt-0.5 leading-tight">Saque instantâneo PIX</p>
                  </div>
                </div>

                {/* Quick Visual Simulator */}
                <div className="mt-4 p-3.5 rounded-2xl bg-emerald-500/[0.08] border border-emerald-500/20">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-emerald-300 flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Simulação de Ganhos
                    </span>
                    <span className="text-[10px] uppercase font-semibold text-slate-300 tracking-wider">
                      Exemplo real
                    </span>
                  </div>
                  <p className="text-xs text-white font-medium">
                    10 indicações = até <strong className="text-emerald-300 font-bold">R$ 600,00</strong> no seu bolso
                  </p>
                  <div className="w-full bg-white/15 h-1.5 rounded-full mt-2.5 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-400 to-teal-300 h-full rounded-full w-2/3" />
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="space-y-2.5 pt-4 mt-2 border-t border-white/15 text-xs text-slate-300">
                  <p className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>Link com atribuição garantida por 30 dias</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>Painel transparente com rastreio em tempo real</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>Sem limites de indicações ou teto de saques</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BecomePartnerCTA({ userName }: { userName?: string }) {
  return (
    <section id="indique" className="py-14 sm:py-18 md:py-24 bg-muted/10 dark:bg-background border-b border-border/60 transition-colors">
      <div className="container-landing">
        <div className="relative rounded-3xl lg:rounded-[2.5rem] bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] dark:from-[#091b2e] dark:via-card dark:to-[#05111e] border border-blue-800/30 dark:border-white/10 shadow-2xl p-7 sm:p-10 lg:p-14 overflow-hidden text-white">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[110px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-emerald-500/15 rounded-full blur-[100px] pointer-events-none" />

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center relative z-10">
            {/* Left Column */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 dark:bg-primary/20 backdrop-blur-md border border-white/20 dark:border-primary/30 text-emerald-300 dark:text-primary text-xs font-semibold uppercase tracking-wider mb-4 shadow-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                <span>Programa de Afiliados</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4 leading-tight">
                Olá, {userName || 'bem-vindo'} 👋 <br />
                <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-400 bg-clip-text text-transparent">
                  Torne-se um parceiro Medikei.
                </span>
              </h2>

              <p className="text-base sm:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-xl">
                Ative sua conta de parceiro gratuitamente e comece a gerar bonificações no PIX indicando consultas médicas para amigos, familiares e pacientes.
              </p>

              {/* 3 Step Sequence */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-8">
                <div className="p-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.11] border border-white/10 hover:border-white/20 transition-all backdrop-blur-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-xl bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 font-bold text-xs flex items-center justify-center">
                      01
                    </span>
                    <UserCheck className="w-4 h-4 text-slate-300/70" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Ative seu perfil</h3>
                  <p className="text-xs text-slate-300/80 mt-1 leading-snug">
                    1 clique no painel, sem burocracia ou taxas.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.11] border border-white/10 hover:border-white/20 transition-all backdrop-blur-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-xl bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 font-bold text-xs flex items-center justify-center">
                      02
                    </span>
                    <Share2 className="w-4 h-4 text-slate-300/70" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Gere seus links</h3>
                  <p className="text-xs text-slate-300/80 mt-1 leading-snug">
                    Links e cupons exclusivos para divulgação.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.11] border border-white/10 hover:border-white/20 transition-all backdrop-blur-xs">
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-xl bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 font-bold text-xs flex items-center justify-center">
                      03
                    </span>
                    <Wallet className="w-4 h-4 text-emerald-300" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Saque no PIX</h3>
                  <p className="text-xs text-slate-300/80 mt-1 leading-snug">
                    Saldo disponível com saque facilitado.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Link to="/parceiro" className="inline-block">
                    <Button
                      variant="primary"
                      size="lg"
                      className="bg-white hover:bg-slate-100 text-[#0c2847] font-extrabold text-base px-8 h-12 rounded-xl shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 group w-full sm:w-auto"
                    >
                      <span>Acessar Painel do Parceiro</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-slate-300/80 flex items-center gap-1.5 pt-1">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Ativação gratuita sem taxa de adesão ou mensalidade.</span>
                </p>
              </div>
            </div>

            {/* Right Column: Benefits Overview */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="w-full max-w-md bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-3xl p-6 sm:p-7 shadow-2xl relative overflow-hidden text-white transition-all">
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

                <div className="flex items-center justify-between pb-4 border-b border-white/15 text-xs">
                  <span className="font-bold text-slate-300 uppercase text-[11px] tracking-wider">
                    Vantagens de Afiliado
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold text-[11px]">
                    100% Gratuito
                  </span>
                </div>

                <div className="mt-5 space-y-3.5 text-sm text-white">
                  <div className="p-3.5 rounded-2xl bg-black/25 border border-white/10 flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="block font-bold text-sm">12% na 1ª consulta</strong>
                      <span className="text-xs text-slate-300">Comissão pelo primeiro atendimento de cada paciente</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-black/25 border border-white/10 flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="block font-bold text-sm">5% de recorrência contínua</strong>
                      <span className="text-xs text-slate-300">Bonificação válida durante 6 meses por paciente ativo</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-black/25 border border-white/10 flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="block font-bold text-sm">Materiais prontos para divulgar</strong>
                      <span className="text-xs text-slate-300">Links customizados, cupons e QR Codes exclusivos</span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-white/15 flex items-center justify-between text-xs text-slate-300">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    Atribuição de 30 dias
                  </span>
                  <span className="font-semibold text-emerald-300">Saque a partir de R$ 50</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PartnerDashboardView() {
  const { user } = useAuth();
  const { data: dashboard, isLoading, error } = usePartnerDashboard(true);

  if (isLoading) {
    return (
      <section id="indique" className="py-14 sm:py-18 md:py-24 bg-muted/10 dark:bg-background border-b border-border/60 transition-colors">
        <div className="container-landing">
          <div className="rounded-3xl lg:rounded-[2.5rem] bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] dark:from-[#091b2e] dark:via-card dark:to-[#05111e] border border-blue-800/30 dark:border-white/10 p-12 text-center flex flex-col justify-center items-center min-h-[340px]">
            <div className="w-10 h-10 border-3 border-emerald-400/20 border-t-emerald-400 rounded-full animate-spin mb-3" />
            <p className="text-sm text-slate-300 font-medium">Carregando painel do parceiro...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !dashboard) {
    return <BecomePartnerCTA userName={user?.name} />;
  }

  return (
    <section id="indique" className="py-14 sm:py-18 md:py-24 bg-muted/10 dark:bg-background border-b border-border/60 transition-colors">
      <div className="container-landing">
        <div className="relative rounded-3xl lg:rounded-[2.5rem] bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] dark:from-[#091b2e] dark:via-card dark:to-[#05111e] border border-blue-800/30 dark:border-white/10 shadow-2xl p-7 sm:p-10 lg:p-14 overflow-hidden text-white">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[110px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-emerald-500/15 rounded-full blur-[100px] pointer-events-none" />

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center relative z-10">
            {/* Left Column */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-semibold uppercase tracking-wider mb-4 shadow-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Painel do Parceiro Ativo</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4 leading-tight">
                Acompanhe seus ganhos <br />
                <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-400 bg-clip-text text-transparent">
                  em tempo real.
                </span>
              </h2>

              <p className="text-base sm:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-xl">
                Compartilhe seu link exclusivo de parceiro e acompanhe cliques, conversões e bonificações creditadas diretamente no seu painel.
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/parceiro/links" className="inline-block">
                  <Button
                    variant="primary"
                    size="lg"
                    className="bg-white hover:bg-slate-100 text-[#0c2847] font-extrabold text-base px-8 h-12 rounded-xl shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 group w-full sm:w-auto"
                  >
                    <span>Ver meus links e extrato</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link
                  to="/parceiro"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 hover:text-emerald-200 hover:underline transition-colors py-2"
                >
                  <span>Painel completo</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Column: Live Performance Card */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="w-full max-w-md bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-3xl p-6 sm:p-7 shadow-2xl relative overflow-hidden text-white transition-all">
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-white/15 text-xs">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-emerald-300" />
                    <span className="font-bold text-slate-300 uppercase text-[11px] tracking-wider">
                      {dashboard.partner?.level ? `Nível ${dashboard.partner.level}` : 'Seu Desempenho'}
                    </span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold text-[11px] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Conectado
                  </span>
                </div>

                {/* Available Balance Box */}
                <div className="mt-5 p-4 rounded-2xl bg-black/25 border border-white/10 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-slate-300 block mb-0.5">Saldo disponível</span>
                    <strong className="text-2xl sm:text-3xl font-black text-white">
                      R$ {(dashboard.balance?.available ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <Link
                    to="/parceiro/saques"
                    className="inline-flex items-center gap-1 text-xs font-bold text-[#0c2847] hover:bg-slate-100 bg-white px-3.5 py-1.5 rounded-full shadow-md transition-all"
                  >
                    <span>Sacar via PIX</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>

                {/* Real Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="p-3.5 rounded-2xl bg-white/[0.05] border border-white/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xl font-bold text-white">{dashboard.stats?.totalClicks ?? 0}</span>
                      <MousePointerClick className="w-4 h-4 text-slate-400" />
                    </div>
                    <p className="text-[11px] text-slate-300">Cliques no link</p>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-white/[0.05] border border-white/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xl font-bold text-emerald-300">
                        {dashboard.stats?.totalConversions ?? 0}
                      </span>
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <p className="text-[11px] text-slate-300">Consultas convertidas</p>
                  </div>
                </div>

                {/* Conversion Rate summary */}
                <div className="mt-4 p-3.5 rounded-2xl bg-emerald-500/[0.08] border border-emerald-500/20 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-300" />
                    <span className="text-slate-300">Taxa de conversão:</span>
                  </div>
                  <strong className="font-bold text-white">
                    {(dashboard.stats?.conversionRate ?? 0).toFixed(1)}%
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ReferralSection() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const isPartner = !!user && (('isPartner' in user && !!(user as { isPartner?: boolean }).isPartner) || (user as { role?: string }).role === 'PARTNER');
  const { isLoading: isDashboardLoading } = usePartnerDashboard(isPartner);

  if (isAuthLoading || (isPartner && isDashboardLoading)) {
    return (
      <section id="indique" className="py-14 sm:py-18 md:py-24 bg-muted/10 dark:bg-background border-b border-border/60 transition-colors">
        <div className="container-landing">
          <div className="rounded-3xl lg:rounded-[2.5rem] bg-gradient-to-br from-[#0c2847] via-[#09223d] to-[#07192d] dark:from-[#091b2e] dark:via-card dark:to-[#05111e] border border-blue-800/30 dark:border-white/10 p-12 text-center flex flex-col justify-center items-center min-h-[340px]">
            <div className="w-10 h-10 border-3 border-emerald-400/20 border-t-emerald-400 rounded-full animate-spin mb-3" />
            <p className="text-sm text-slate-300 font-medium">Carregando informações do programa...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!user) {
    return <LoginCTA />;
  }

  if (!isPartner) {
    return <BecomePartnerCTA userName={user.name} />;
  }

  return <PartnerDashboardView />;
}
